import api from "@/util/api"
import moment from "moment"
import router from '@/router'
import i18n from "@/translations/i18n";
import _ from 'lodash'

const MAX_API_ATTEMPTS = 3;

const cartDefaults = {
  cartGuid: '',
  expiredCartGuid: "",
  deliveryOptions: [],
  paymentOptions: [],
  activeDeliveryCode: "",
  activeDeliveryOptionIndex: 0,
  activeDeliveryOptionCost: 0,
  numberOfItemsInCart: 0,
  cartExpiryTime: null,
  prepLineItems: {},
  inventoryIdsReservedList: [],
  aggregatedCart: {},
  transaction: {},
  successfulCartAdditions: [],
  failedCartAdditions: [],
  membersOnlyTicketSelected: false,
  membersOnlyErrorReason: "",
  cartSubTotal: 0,
  timerHasBeenSet: false,
}

export const state = {
  cartGuid: cartDefaults.cartGuid,
  expiredCartGuid: cartDefaults.expiredCartGuid,
  deliveryOptions: cartDefaults.deliveryOptions,
  paymentOptions: cartDefaults.paymentOptions,
  activeDeliveryCode: cartDefaults.activeDeliveryCode,
  activeDeliveryOptionIndex: cartDefaults.activeDeliveryOptionIndex,
  activeDeliveryOptionCost: cartDefaults.activeDeliveryOptionCost,
  numberOfItemsInCart: cartDefaults.numberOfItemsInCart,
  cartExpiryTime: cartDefaults.cartExpiryTime,
  prepLineItems: cartDefaults.prepLineItems,
  inventoryIdsReservedList: cartDefaults.inventoryIdsReservedList,
  aggregatedCart: cartDefaults.aggregatedCart,
  transaction: cartDefaults.transaction,
  successfulCartAdditions: cartDefaults.successfulCartAdditions,
  failedCartAdditions: cartDefaults.failedCartAdditions,
  membersOnlyTicketSelected: cartDefaults.membersOnlyTicketSelected,
  membersOnlyErrorReason: cartDefaults.membersOnlyErrorReason,
  cartSubTotal: cartDefaults.cartSubTotal,
  timerHasBeenSet: cartDefaults.timerHasBeenSet,
}

export const mutations = {
  SET_CART_GUID(state, guid) {
    state.cartGuid = guid
  },
  // When a cart expires, we use this expiredCartGuid to release seats back into the wild
  SET_EXPIRED_CART_GUID(state, guid) {
    state.expiredCartGuid = guid
  },
  SET_PAYMENT_OPTIONS(state, options) {
    state.paymentOptions = options
  },
  SET_DELIVERY_OPTIONS(state, options) {
    state.deliveryOptions = options

    // WKCDA-1403 - Adding a blank option in front of all other delivery options
    state.deliveryOptions.unshift({
      addressRequired: false,
      charge: {
        amount: 0,
        currency: "HKD",
        formatted: "HKD0.00"
      },
      code: "BLANK_DELIVERY_OPTION",
      feeWaived: false,
      order: -1
    })
  },
  SET_ACTIVE_DELIVERY_CODE(state, code) {
    state.activeDeliveryCode = code
  },
  SET_ACTIVE_DELIVERY_OPTION_INDEX(state, option) {
    state.activeDeliveryOptionIndex = option
  },
  SET_ACTIVE_DELIVERY_OPTION_COST(state, cost) {
    state.activeDeliveryOptionCost = cost;
  },
  SET_NUMBER_OF_ITEMS_IN_CART(state, itemCount) {
    state.numberOfItemsInCart = itemCount;
  },
  SET_CART_EXPIRY_TIME(state, time) {
    state.cartExpiryTime = time
  },
  SET_PREP_LINE_ITEMS(state, value) {
    state.lineItemList = value;
  },
  SET_INVENTORY_IDS_RESERVED_LIST(state, value) {
    state.inventoryIdsReservedList = value;
  },
  SET_AGGREGATED_CART(state, cart) {
    state.aggregatedCart = cart
  },
  SET_TRANSACTION_INFO(state, info) {
    state.transaction = info
  },
  SET_SUCCESSFUL_SUBMISSIONS(state, value) {
    state.successfulCartAdditions = value;
  },
  ADD_SUCCESSFUL_SUBMISSION(state, item) {
    state.successfulCartAdditions.push(item)
  },
  SET_FAILED_SUBMISSIONS(state, value) {
    state.failedCartAdditions = value;
  },
  ADD_FAILED_SUBMISSION(state, item) {
    state.failedCartAdditions.push(item)
  },
  SET_MEMBERS_ONLY_TICKET_SELECTED(state, value) {
    state.membersOnlyTicketSelected = value
  },
  SET_MEMBERS_ERROR_REASON(state, reason) {
    state.membersOnlyErrorReason = reason;
  },
  SET_CART_SUBTOTAL(state, cost) {
    // A number used for some validation
    state.cartSubTotal = cost;
  },
  SET_TIMER_HAS_BEEN_SET(state, value) {
    state.timerHasBeenSet = value
  }
}

export const actions = {
  updateCartGuid({ commit }, guid) {
    commit('SET_CART_GUID', guid)
  },
  updateActiveDeliveryOption({ commit, state }, item) {
    commit('SET_ACTIVE_DELIVERY_OPTION_INDEX', item)
    commit('SET_ACTIVE_DELIVERY_CODE', state.deliveryOptions[item].code)
    commit('SET_ACTIVE_DELIVERY_OPTION_COST', state.deliveryOptions[item].charge.amount);
  },
  updateInventoryReservedList({ commit }, unsortedSeats) {
    // Sort the inventory IDs. This way if we fail to add any seats, it should at least maintain some seat groupings
    let sortedList = unsortedSeats.sort((a, b) => { a > b })
    commit('SET_INVENTORY_IDS_RESERVED_LIST', sortedList);
  },
  clearinventoryIdsReservedList({ commit }) {
    commit('SET_INVENTORY_IDS_RESERVED_LIST', []);
  },
  updateCartExpiryTime({ state, commit }, info) {
    if (state.timerHasBeenSet == false) {
      let expiryTime = new moment(info.time);
      expiryTime.add(30, "minutes");

      commit('SET_CART_EXPIRY_TIME', expiryTime)
      commit('SET_TIMER_HAS_BEEN_SET', true)
    }
  },
  async sendToCart({ commit, dispatch, state }, payload) {
    let attempts = 0;

    let nonRepeatErrors = [
      'error.cart.add-to-cart.fail',
      'error.cart.inventory.retrieve.fail',
      'error.cart.inventory.duplicates',
      'error.cart.inventory.quantity-price-class.mismatch',
      'error.cart.add-to-cart.redemption.quota-exceeded',
      'error.cart.membership.rule.no-valid-membership',
      'error.cart.membership.redeem.quota-reached',
      'error.cart.membership.connection-issue',
      'error.cart.sales-quota.transaction.exceeded',
      'error.cart.postcommit.exceed-allocation',
      'error.cart.postcommit.exceed-quota',
      'error.cart.inventory.seat.insufficient',
      'error.cart.package.mix-cart.fail',
      'error.cart.add-event.mix-not-allowed',
      'error.cart.new-event.mix-not-allowed',
      'error.package.promopassword.quota.reached',
      'error.package.promopassword.not.validated',
      'error.cart.promo.code.hold-code.no-config',
      'error.cart.promo.code.invalid',
      'error.cart.promo.code.price-class.mismatch',
      'error.cart.promo.password.empty',
      'error.cart.promo.password.inactive',
      'error.cart.promo.code.popup.text',
      'error.cart.promo.password.quota-reached',
      'error.cart.promo.password.incorrect',
      'error.cart.postcommit.exceed-max-qty',
      'error.cart.inventory.empty',
      'error.cart.inventory.quantity-price-class.mismatch'
    ]

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.postTicketsToBasket(payload);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          let oldCartGuid = ''

          if (state.cartGuid != '') {
            oldCartGuid = state.cartGuid;
          }

          // If the cart ID has changed, it's a new cart and we'll need to restart the timer.
          if (oldCartGuid !== response.data.data.cartGuid) {
            commit("SET_CART_GUID", response.data.data.cartGuid);
          }

          // Make sure we start the timer only once for the same cart.
          if (state.cartExpiryTime === cartDefaults.cartExpiryTime) {
            dispatch('updateCartExpiryTime', { time: response.data.data.reservedTime })
          }

          // Keep track of successful submissions in case any fail
          commit('ADD_SUCCESSFUL_SUBMISSION', payload)

          // We could find out the exact number by making a getCart call here
          // but for client performance; we'll just set to 1 - it'll be updated once they reach shopping-cart
          if (state.numberOfItemsInCart === 0) commit("SET_NUMBER_OF_ITEMS_IN_CART", 1)

          return true;
        } else {
          throw response;
        }
      } catch (e) {
        failedSendToCart(e, payload);
      }
    }

    function failedSendToCart(e, payload) {
      let errorContent = _.get(e.response.data, 'error.source.content')

      if (nonRepeatErrors.includes(errorContent)) {
        attempts = MAX_API_ATTEMPTS;
      }

      if (attempts == MAX_API_ATTEMPTS) {
        if (errorContent == "error.cart.membership.rule.no-valid-membership" || errorContent == "error.cart.membership.redeem.quota-reached" || errorContent == "error.cart.membership.connection-issue") {
          // error message handled by members-only-modal (no need to updateError)
          commit("SET_MEMBERS_ONLY_TICKET_SELECTED", true);
          commit("SET_MEMBERS_ERROR_REASON", errorContent);
        }

        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent)
          dispatch('updateFallbackError', "shopping_cart_items_retrieval_failed")
        } else {
          dispatch('updateError', "shopping_cart_items_retrieval_failed")
        }

        // Keep track of failed submissions to display on choose-tickets
        commit('ADD_FAILED_SUBMISSION', payload)

        return payload ? { error: e, payload: payload } : { error: e };
      }
    }
  },
  async returnItemsForSelection({ dispatch, state }, productId) {
    // No items to return
    if (state.inventoryIdsReservedList.length == 0) {
      return;
    }

    dispatch('updateLoading', true);

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let itemsToReturn = [];
        state.inventoryIdsReservedList.forEach(item => {
          if (typeof item === 'object') {
            itemsToReturn.push(item.inventoryId);
          } else {
            itemsToReturn.push(item);
          }
        });

        let response = await api.postReleaseSeats(productId, { releasedSeatList: itemsToReturn });

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;
          endReturnItems("success");
        } else {
          throw response;
        }
      } catch (e) {
        endReturnItems("failed", e);
      }
    }

    function endReturnItems(status, error = "") {
      if (attempts === MAX_API_ATTEMPTS) {
        if (status == "success") {
          dispatch('clearinventoryIdsReservedList');
        }

        dispatch('updateLoading', false);

        if (status == "failed") {
          console.error("Failed to return items, reason (if any) retrieved: ", error)
        }
      }
    }
  },
  async destroyCartInstance({ commit, state, dispatch, rootState }, context = "timeout") {
    await dispatch('returnItemsForSelection', rootState.performance.productId)

    // Prep for cart clean up.
    commit('SET_EXPIRED_CART_GUID', state.cartGuid);

    // clear the other store values like delivery, seats, etc
    // (the ones that are empty at the beginning)
    commit('SET_NUMBER_OF_ITEMS_IN_CART', cartDefaults.numberOfItemsInCart);
    commit('SET_CART_EXPIRY_TIME', cartDefaults.cartExpiryTime);
    commit('SET_TIMER_HAS_BEEN_SET', cartDefaults.timerHasBeenSet);
    commit('SET_CART_GUID', cartDefaults.cartGuid);

    if (context != "cart-confirmation") {
      commit('SET_ACTIVE_DELIVERY_OPTION_COST', cartDefaults.activeDeliveryOptionCost);
      commit('SET_AGGREGATED_CART', Object.assign({}));

      // get the expired cart items from the cart -
      // delete them one by one (this will clear the reserved items in Sistic)
      const itemToDelete = {};
      Object.keys(state.prepLineItems).forEach((key) => {
        itemToDelete[key] = Obj.key;
        dispatch('removeCartItem', { itemToDelete: itemToDelete, context: "timeout" })
      });

      dispatch('updateLoading', false);

      // Not redirecting to /cart in debug mode for debugging purposes
      if (context == "timeout") {
        let windowPath = window.location.pathname.toString()

        // Go to Cart page if we're not already there
        if (!windowPath.includes('/cart')) {
          router.push({ name: 'shopping-cart', locale: i18n.locale });
        }
      }
    }

    // Clearing prepLineItems after we've completed clearing them above
    commit('SET_PREP_LINE_ITEMS', cartDefaults.prepLineItems);
  },
  async removeCartItem({ state, dispatch }, info) {
    dispatch('updateLoading', true)

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response;
        if (info.context == "timeout") {
          response = await api.deleteCartItem(
            state.expiredCartGuid,
            info.itemToDelete
          );
        } else {
          response = await api.deleteCartItem(
            state.cartGuid,
            info.itemToDelete
          );
        }

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;
          dispatch('getCartItems');
        } else {
          throw response;
        }
      } catch (e) {
        failedToRemoveItem(e);
      }

      function failedToRemoveItem(e) {
        let errorContent = _.get(e.response.data, 'error.source.content');
        if (attempts == MAX_API_ATTEMPTS) {

          if (errorContent) {
            // Preferentially use SISTIC error code
            dispatch('updateSisticError', errorContent)
            dispatch('updateFallbackError', 'generic_error');
          } else {
            dispatch('updateError', 'generic_error');
          }

          dispatch('updateLoading', false)
        }
      }
    }
  },
  async getCartItems({ commit, dispatch, state }, context) {
    let attempts = 0;
    dispatch('updateLoading', true);


    if (!state.cartGuid) {
      // If we have no cart, do not attempt a request.
      dispatch('destroyCartInstance', "empty-cart");
      dispatch('clearError');
      dispatch('updateLoading', false);
      return;
    }

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getCart(state.cartGuid, i18n.locale);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;
          dispatch('updateLoading', false);
          dispatch('clearError');

          if (response.data.data.lineItemList == undefined) {
            // If the user has no items in the basket, destroy it so they generate a new one next time
            // the add an item.
            dispatch('destroyCartInstance', "empty-cart")
          } else {
            if (context != "cart-confirmation") {
              dispatch('processDeliveryOptions', response.data.data.commonDeliveryMethod.deliveryMethodList);
              dispatch('processPaymentOptions', response.data.data.commonPaymentMethod.paymentMethodList);
            }

            commit('SET_NUMBER_OF_ITEMS_IN_CART', response.data.data.lineItemList.length);

            // We keep track of the current CartItem data in order to be able to
            // delete them easily in the background on cart expiry.
            const prepLineItems = {};

            let lineItemList = response.data.data.lineItemList;
            lineItemList.forEach((item) => {
              prepLineItems.cartItemId = item.cartItemId;
              prepLineItems.priceClassCode = item.priceclass.priceClassCode;
            });

            commit('SET_PREP_LINE_ITEMS', prepLineItems);

            // Clean up any information which may cause conflicts on future additions
            // We do this at this stage, as some items have to loop through cart additions (above)
            dispatch('updateInventoryReservedList', []);

            // Send the lineItemList to be processed
            // We don't receive it aggregated by cart initially
            // There's some SISTIC logic around how items are grouped
            dispatch('aggregateCartItems', response.data.data.lineItemList);

            commit('SET_CART_SUBTOTAL', response.data.data.lineItemTotal.amount)
          }
        } else {
          throw response;
        }
      } catch (e) {
        failedToGetCart(e);
      }
    }

    function failedToGetCart(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      if (errorContent == 'error.cart.not-found') {
        attempts = MAX_API_ATTEMPTS;
      }

      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);

        if (e.response.status == 401 || e.response.status == 403) {
          // Not authorized / Forbbidden; 
          dispatch('updateError', "shopping_cart_items_needs_login");
        } else {
          if (errorContent) {
            // Preferentially use SISTIC error code
            dispatch('updateSisticError', errorContent)
            dispatch('updateFallbackError', 'generic_error');
          } else {
            dispatch('updateError', 'generic_error');
          }
        }
      }
    }
  },
  processPaymentOptions({ commit }, options) {
    let processedOptions = options.map((option) => {
      return {
        value: option.code,
        label: getPaymentranslation(option.code),
        image: option.code
      };
    });

    function getPaymentranslation(type) {
      let translationReference = "payment_methods." + type;
      let translationExists = i18n.te(translationReference);

      if (translationExists) {
        return i18n.t(translationReference);
      } else {
        return type;
      }

    }
    commit('SET_PAYMENT_OPTIONS', processedOptions)
  },
  processDeliveryOptions({ state, commit, dispatch }, options) {
    commit('SET_DELIVERY_OPTIONS', options)
    dispatch('updateActiveDeliveryOption', 0)

    // Note the getDeliveryTranslation is a regular JS function (hence, no dispatch)
    state.deliveryOptions.forEach((option) => {
      option["translated_description"] = getDeliveryTranslation("description", option);
      option["translated_title"] = getDeliveryTranslation("title", option);
    });

    function getDeliveryTranslation(type, option) {
      let translationReference = "delivery_methods." + option.code + "." + type;
      // i18n.te == Translation Exists | returns Boolean
      let translationExists = i18n.te(translationReference);

      if (translationExists) {
        return i18n.t(translationReference);
      } else {
        if (type == "title") {
          return option.code;
        } else {
          return "";
        }
      }
    }
  },
  aggregateCartItems({ commit }, items) {
    let indexedCart = {}

    // Aggregate tickets for the same event that would be separate items in the cart.
    // go through each element, grab its productID
    // assign product ID as a key where we can aggregate tickets for the same event
    // the key's content should be the element itself (in an array)
    items.forEach((item) => {
      let productId = item.product.productId;

      // key already exists, push item to the array
      if (indexedCart.hasOwnProperty(productId)) {
        indexedCart[productId].push(item);
      } else {
        // key doesn't exist, add the item (as the first element of an array)
        indexedCart[productId] = [item];
      }
    });

    /*
     * Vue cannot detect property addition or deletion of Objects
     * the below forces a reactivity by creating a new object with all the items in it
     * https://vuejs.org/v2/guide/reactivity.html#For-Objects
     */
    commit("SET_AGGREGATED_CART", Object.assign({}, indexedCart));
  },
  async getTransaction({ commit, dispatch }, transactionId) {
    dispatch('updateLoading', true)

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getTransaction(transactionId);

        if (response.status === 200) {
          dispatch('updateLoading', false)
          commit('SET_TRANSACTION_INFO', response.data)
          attempts = MAX_API_ATTEMPTS;
        } else {
          throw response;
        }
      } catch (e) {
        failedToGetTransactionInfo(e);
      }
    }

    function failedToGetTransactionInfo(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);

        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent)
          dispatch('updateFallbackError', 'generic_error');
        } else {
          dispatch('updateError', 'generic_error');
        }
      }

    }
  },
  clearCartAdditionTracking({ commit }) {
    commit('SET_SUCCESSFUL_SUBMISSIONS', cartDefaults.successfulCartAdditions)
    commit('SET_FAILED_SUBMISSIONS', cartDefaults.failedCartAdditions)
  },
  updateMembersOnlyTicketSelected({ commit }, value) {
    commit('SET_MEMBERS_ONLY_TICKET_SELECTED', value)
  },
  checkReservedTimer({ state, dispatch }) {
    if (state.cartGuid == cartDefaults.cartGuid && state.cartExpiryTime != cartDefaults.cartExpiryTime) {
      dispatch('destroyCartInstance', "check-reserved-timer")
    }
  },
  clearSeatsReservedTimerCheck({ commit, getters }) {
    if (getters.timerOnButCartIsEmpty) {
      commit('SET_TIMER_HAS_BEEN_SET', cartDefaults.timerHasBeenSet);
    }
  },
  clearMembersErrorMessage({commit}) {
    commit("SET_MEMBERS_ERROR_REASON", "");
  }
}

export const getters = {
  getDeliveryOption: state => {
    let options = state.deliveryOptions;
    let activeOption = state.activeDeliveryOptionIndex;

    return options[activeOption];
  },
  getPaymentOption: state => {
    let options = state.paymentOptions;

    return options
  },
  cartIsEmpty: state => {
    return state.cartGuid.length == 0;
  },
  someCartAdditionsFailed: (state) => {
    return state.failedCartAdditions.length > 0;
  },
  getTransactionReference: (state) => {
    return _.get(state.transaction, 'sisticTransaction.transactionRefNo', false);
  },
  getETicketUrl: (state) => {
    return _.get(state.transaction, 'sisticConfirmation.deliveryMethod.eticketURL', false);
  },
  getTransactionDate: (state) => {
    return _.get(state.transaction, 'sisticTransaction.transactedTime', null);
  },
  getPaymentMethod: (state) => {
    let paymentMethod = _.get(state.transaction, 'sisticTransaction.paymentMethod', null);

    if (paymentMethod == " ") {
      // This is the value received in E-ticket payments
      return null;
    } else {
      return paymentMethod;
    }
  },
  activeDeliveryDescription: (state) => {
    return _.get(state.deliveryOptions[state.activeDeliveryOptionIndex], 'translated_description', "");
  },
  addressRequired: (state) => {
    return _.get(state.deliveryOptions[state.activeDeliveryOptionIndex], 'addressRequired', false)
  },
  cartTotalCost: (state) => {
    return state.cartSubTotal + state.activeDeliveryOptionCost;
  },
  showComplementary: (state) => {
    return state.cartSubTotal === 0 && state.activeDeliveryOptionCost === 0
  },
  timerOnButCartIsEmpty: (state) => {
    return state.numberOfItemsInCart == 0 && state.timerHasBeenSet
  }
}